import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdCloudUpload, MdDownload, MdFilterList, MdRefresh } from 'react-icons/md';
import Card from 'components/card/Card';
import {
  ApiCoverageInputParameters,
  ApiDownloadCoverageParameter,
} from 'api/coverage/inputParameters';
import { ApiForecastList, ForecastListItem } from 'api/coverage/forecastList';
import { useLoading } from 'contexts/LoadingContext';
import { ForecastUploadModal } from './ForecastUploadModal';

export type ForecastRow = {
  id: string;
  producto: string;
  fecha: string;
  unidades: number;
};

function formatIsoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function mapForecastItem(item: ForecastListItem): ForecastRow {
  return {
    id: String(item.id),
    producto: item.product_code,
    fecha: item.forecasted_date,
    unidades: item.units,
  };
}

export default function ForecastAdmin(): JSX.Element {
  const toast = useToast();
  const { show, hide } = useLoading();
  const uploadModal = useDisclosure();

  const [rows, setRows] = useState<ForecastRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [forecastParamId, setForecastParamId] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  }, []);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 30);
    return formatIsoDay(d);
  });
  const [dateTo, setDateTo] = useState(() => formatIsoDay(today));

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const subText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const rowAlt = useColorModeValue('white', 'navy.900');
  const filterShellBg = useColorModeValue(
    'linear-gradient(165deg, rgba(14, 165, 233, 0.06) 0%, rgba(15, 118, 110, 0.04) 55%, rgba(248, 250, 252, 0.9) 100%)',
    'linear-gradient(165deg, rgba(14, 165, 233, 0.12) 0%, rgba(15, 118, 110, 0.08) 100%)',
  );
  const filterShellBorder = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');
  const filterAccent = useColorModeValue('teal.500', 'teal.300');
  const selectFieldBg = useColorModeValue('white', 'navy.800');

  useEffect(() => {
    ApiCoverageInputParameters()
      .then((res) => {
        const list = res.data as Array<{ id: number; name: string }>;
        const fp = list.find((p) => p.name === 'products_forecast');
        setForecastParamId(fp?.id ?? null);
      })
      .catch(() => {
        setForecastParamId(null);
      });
  }, []);

  const fetchForecastList = useCallback(() => {
    if (dateFrom > dateTo) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    setListLoading(true);
    ApiForecastList({
      date_from: dateFrom,
      date_to: dateTo,
      page,
      page_size: pageSize,
    })
      .then((res) => {
        setRows(res.data.results.map(mapForecastItem));
        setTotalCount(res.data.count);
      })
      .catch(() => {
        setRows([]);
        setTotalCount(0);
        toast({
          title: 'Error al cargar forecast',
          description: 'No se pudo obtener el listado desde el servidor.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => setListLoading(false));
  }, [dateFrom, dateTo, page, pageSize, toast]);

  useEffect(() => {
    fetchForecastList();
  }, [fetchForecastList, reloadToken]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, pageCount);
  const hasInvalidRange = dateFrom > dateTo;

  const handleServerDownload = () => {
    if (hasInvalidRange) {
      toast({
        title: 'Rango inválido',
        description: 'La fecha inicial no puede ser posterior a la fecha final.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (forecastParamId == null) {
      toast({
        title: 'Descarga no configurada',
        description: 'No se encontró el parámetro de forecast en el servidor.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    show();
    ApiDownloadCoverageParameter(forecastParamId, null, {
      date_from: dateFrom,
      date_to: dateTo,
    })
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        a.download = `Forecast_${dateFrom}_${dateTo}_${stamp}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
          title: 'Descarga lista',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          title: 'Error al descargar',
          description: 'No se pudo generar el archivo de forecast.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => hide());
  };

  return (
    <Box pt={{ base: '120px', md: '75px' }} px={{ base: '20px', md: '30px' }}>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Box px="25px" mb="20px">
          <Flex
            mb={4}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={3}>
            <Text color="secondaryGray.900" fontSize="xl" fontWeight="700" letterSpacing="-0.02em">
              Forecast
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<MdRefresh />}
                isLoading={listLoading}
                onClick={() => fetchForecastList()}>
                Actualizar
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                variant="outline"
                leftIcon={<MdCloudUpload />}
                alignSelf={{ base: 'stretch', md: 'auto' }}
                onClick={uploadModal.onOpen}>
                Cargar forecast
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<MdDownload />}
                onClick={handleServerDownload}>
                Descargar forecast
              </Button>
            </HStack>
          </Flex>
          <Divider mb={4} />
          <Box
            position="relative"
            overflow="hidden"
            borderRadius="xl"
            borderWidth="1px"
            borderColor={filterShellBorder}
            bg={filterShellBg}
            px={{ base: 4, md: 5 }}
            py={5}
            boxShadow="sm">
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              w="4px"
              bgGradient="linear(to-b, teal.400, cyan.500)"
              aria-hidden
            />
            <Flex align="center" gap={2} mb={4} pl={1}>
              <Icon as={MdFilterList} color={filterAccent} boxSize="18px" />
              <Text
                fontSize="xs"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.14em"
                color={subText}>
                Filtros del forecast
              </Text>
            </Flex>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacingY={4} spacingX={4}>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700" mb={1.5} color={subText}>
                  Desde
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  bg={selectFieldBg}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700" mb={1.5} color={subText}>
                  Hasta
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  bg={selectFieldBg}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </FormControl>
            </SimpleGrid>
          </Box>
        </Box>
        <Box overflowX="auto" px="25px" pb={6}>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Producto</Th>
                <Th>Fecha</Th>
                <Th isNumeric>Unidades</Th>
              </Tr>
            </Thead>
            <Tbody>
              {listLoading && rows.length === 0 ? (
                <Tr>
                  <Td colSpan={3}>
                    <Text fontSize="sm" color={subText} py={6} textAlign="center">
                      Cargando forecast…
                    </Text>
                  </Td>
                </Tr>
              ) : null}
              {!listLoading && rows.length === 0 ? (
                <Tr>
                  <Td colSpan={3}>
                    <Text fontSize="sm" color={subText} py={6} textAlign="center">
                      {hasInvalidRange
                        ? 'La fecha inicial no puede ser posterior a la fecha final.'
                        : 'No hay registros en este rango. Amplía las fechas.'}
                    </Text>
                  </Td>
                </Tr>
              ) : null}
              {rows.map((row, idx) => {
                const bgRow = idx % 2 === 0 ? rowAlt : 'transparent';
                return (
                  <Tr key={row.id} bg={bgRow}>
                    <Td fontWeight="600">{row.producto}</Td>
                    <Td>
                      {new Date(`${row.fecha}T12:00:00`).toLocaleDateString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Td>
                    <Td isNumeric>{row.unidades.toLocaleString('es-MX')}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <Flex mt={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <Text fontSize="sm" color={subText}>
              Mostrando {rows.length} de {totalCount} registros
            </Text>
            <HStack>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage <= 1 || listLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Anterior
              </Button>
              <Text fontSize="sm">
                Página {currentPage} / {pageCount}
              </Text>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage >= pageCount || listLoading}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                Siguiente
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Card>

      <ForecastUploadModal
        isOpen={uploadModal.isOpen}
        onClose={uploadModal.onClose}
        onUploaded={() => {
          setReloadToken((t) => t + 1);
          toast({
            title: 'Carga enviada',
            description: 'El archivo fue procesado correctamente.',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        }}
      />
    </Box>
  );
}
